import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Plus, Building2, ShieldCheck, PauseCircle, PlayCircle, CheckCircle2, Save } from "lucide-react";

const EMPTY_FORM = { name: "", slug: "", timezone: "America/New_York", content_language: "en", owner_email: "" };

const STATUS_BADGE = {
  active: "bg-[#10B981]/10 text-[#10B981]",
  suspended: "bg-[#EF4444]/10 text-[#EF4444]",
};

export default function ClientsPage() {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [capEdits, setCapEdits] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/clients");
      setClients(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la lista de clientes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY_FORM); setOpen(true); };

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/platform/clients", form);
      toast.success("Cliente creado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el cliente");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (client) => {
    const nextStatus = client.status === "active" ? "suspended" : "active";
    try {
      const r = await api.patch(`/platform/clients/${client.tenant_id}`, { status: nextStatus });
      toast.success(nextStatus === "suspended" ? "Cliente suspendido" : "Cliente reactivado");
      if (r.data.publications_needing_reschedule > 0) {
        toast.warning(
          `${r.data.publications_needing_reschedule} publicación(es) quedaron pendientes de reprogramar manualmente (no se publican solas).`
        );
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el estado");
    }
  };

  const grantAccess = async (client) => {
    try {
      await api.post(`/platform/clients/${client.tenant_id}/grant-me-access`);
      toast.success(`Ahora tenés acceso admin a ${client.name} (queda registrado en el audit log)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo dar acceso");
    }
  };

  const saveCap = async (client) => {
    const raw = capEdits[client.tenant_id];
    const value = raw === "" || raw === undefined ? null : parseFloat(raw);
    if (value !== null && (Number.isNaN(value) || value <= 0)) {
      toast.error("El tope debe ser un número mayor a 0, o vacío para sacarlo");
      return;
    }
    try {
      await api.patch(`/platform/clients/${client.tenant_id}`, { monthly_cost_cap_usd: value });
      toast.success(value === null ? "Tope sacado (sin límite)" : "Tope actualizado");
      setCapEdits((prev) => { const next = { ...prev }; delete next[client.tenant_id]; return next; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el tope");
    }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A]">
          Clientes de la plataforma. Cada uno es un tenant aislado: nada se comparte entre ellos.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-client-button" onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
              <Plus className="h-4 w-4 mr-1" /> Nueva empresa
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-client-dialog">
            <DialogHeader><DialogTitle className="font-heading">Nueva empresa</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Nombre</Label>
                <Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Slug (va en /b/slug, único, sin espacios)</Label>
                <Input
                  data-testid="client-slug-input" value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                  placeholder="mi-empresa"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Email del dueño (admin)</Label>
                <Input
                  data-testid="client-owner-email-input" type="email" value={form.owner_email}
                  onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Zona horaria</Label>
                <Input data-testid="client-timezone-input" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="save-client-button" onClick={create}
                disabled={saving || !form.name || !form.slug || !form.owner_email}
                className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Crear
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : clients.length === 0 ? (
          <div className="py-16 text-center">
            <Building2 className="h-8 w-8 text-[#A1A1AA] mx-auto" />
            <p className="mt-3 text-sm text-[#71717A]">Todavía no hay clientes dados de alta.</p>
          </div>
        ) : (
          <Table data-testid="clients-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nombre</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Dueño</TableHead>
                <TableHead>Usuarios</TableHead>
                <TableHead>Cuentas conectadas</TableHead>
                <TableHead>Publicaciones (mes)</TableHead>
                <TableHead>Leads (mes)</TableHead>
                <TableHead>Google</TableHead>
                <TableHead>Costo (mes, estimado)</TableHead>
                <TableHead>Tope mensual</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((c) => (
                <TableRow key={c.tenant_id} data-testid={`client-row-${c.slug}`}>
                  <TableCell className="font-medium">
                    {c.name}
                    <div className="text-xs text-[#71717A] font-normal">/{c.slug}</div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      data-testid={`client-status-${c.slug}`}
                      className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${STATUS_BADGE[c.status]}`}
                    >
                      {c.status === "active" ? "Activo" : "Suspendido"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[#71717A]">{c.owner_email || "—"}</TableCell>
                  <TableCell className="text-[#71717A]">{c.users_count}</TableCell>
                  <TableCell className="text-[#71717A]">{c.connected_accounts_count}</TableCell>
                  <TableCell className="text-[#71717A]">{c.publications_month}</TableCell>
                  <TableCell className="text-[#71717A]">{c.leads_month}</TableCell>
                  <TableCell>
                    {c.google_connected
                      ? <CheckCircle2 className="h-4 w-4 text-[#10B981]" />
                      : <span className="text-[#A1A1AA] text-xs">no</span>}
                  </TableCell>
                  <TableCell className="text-[#71717A]" data-testid={`client-cost-${c.slug}`}>
                    ${(c.cost_month_usd || 0).toFixed(2)}
                    {c.monthly_cost_cap_usd && c.cost_month_usd >= c.monthly_cost_cap_usd && (
                      <span className="ml-1 text-[9px] uppercase text-[#EF4444] font-medium">tope alcanzado</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number" min="0" step="1" placeholder="sin tope"
                        className="h-7 w-20 text-xs"
                        data-testid={`client-cap-input-${c.slug}`}
                        value={capEdits[c.tenant_id] ?? (c.monthly_cost_cap_usd ?? "")}
                        onChange={(e) => setCapEdits((prev) => ({ ...prev, [c.tenant_id]: e.target.value }))}
                      />
                      <Button
                        variant="ghost" size="sm" className="h-7 px-2 text-[#0055FF]"
                        data-testid={`client-cap-save-${c.slug}`} onClick={() => saveCap(c)}
                      >
                        <Save className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost" size="sm" onClick={() => toggleStatus(c)}
                      data-testid={`toggle-status-${c.slug}`}
                      className={c.status === "active" ? "text-[#EF4444]" : "text-[#10B981]"}
                    >
                      {c.status === "active"
                        ? <><PauseCircle className="h-4 w-4 mr-1" /> Suspender</>
                        : <><PlayCircle className="h-4 w-4 mr-1" /> Reactivar</>}
                    </Button>
                    <Button
                      variant="ghost" size="sm" onClick={() => grantAccess(c)}
                      data-testid={`grant-access-${c.slug}`} className="text-[#0055FF]"
                    >
                      <ShieldCheck className="h-4 w-4 mr-1" /> Darme acceso
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
